/* eslint-disable no-bitwise, eqeqeq */
import {
  postSizerMatrixData,
  saveSizerMatrixData,
  type ISizerCabinetDimensions,
  type ISizerCell,
  type ISizerDeviation,
  type ISizerMatrixRequest,
  type ISizerMatrixRow,
  type ISizerOptimalSize,
} from 'app/services/sizerApi';
import {
  buildOptimalGraph,
  cutAreas,
  deviationWindow,
  enlargeArea,
  interpolateExtrapolate,
  optimalAxisIndex,
  shrinkAreaToFitAvailableSpace,
  snapOptimalIndex,
  timeout,
  type MatrixArea,
} from './matrixMath';

/**
 * Phase 4c/4d — a 1:1 port of `django/sizer/static/sizer/mvc/view/matrix.js`
 * (593 lines), the manual windowing engine behind the sizer grid.
 *
 * ★ WHY THIS IS IMPERATIVE AND NOT JSX. The grid is 277 × 277 = 76 729 cells on
 * a 26 592 × 19 390 px canvas; only ~280-670 of them exist as DOM at any moment
 * (OBSERVED on dev3 2026-07-28: 280 at load, 391 after a Size click, 782 after a
 * scroll gesture). Legacy achieves that by APPENDING and REMOVING individual
 * absolutely-positioned nodes and never touching the ones already in place —
 * blocks survive across scrolls until they drift 25 cells outside the window.
 * Rendering the same set declaratively would rebuild attributes on every scroll
 * tick, lose the "already rendered ⇒ skip" fast path (`:444-445`), and lose the
 * row-by-row `await timeout(1)` yield (`:456`) that keeps the page painting.
 * The repo has no virtualization library, so the parity-first choice from the
 * discovery's §11 ("port matrix.js's manual windowing verbatim") is what this
 * is. React owns the SCAFFOLD (`tmpl/matrix.tmpl`); this engine owns the
 * children of `#main-table` and nothing else.
 *
 * ★ THE ONE STRUCTURAL ADDITION: `destroy()`. Legacy never clears its 700 ms
 * interval, never unbinds `container.onscroll` and has no way to stop in-flight
 * renders — it simply leaks when `render(true)` builds a second MatrixView
 * (main.js:327). React must not, so every async continuation checks
 * `this.destroyed` before touching the DOM.
 */

// ---------------------------------------------------------------------------
// the cell template — tmpl/block.tmpl
// ---------------------------------------------------------------------------

/** Handlebars' default `{{ }}` escaping; `null`/`undefined` render as ''. */
function esc(value: unknown): string {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

export interface BlockTemplateData extends ISizerCell {
  page_x: number;
  page_y: number;
  x_on_page: number;
  y_on_page: number;
  current_height: string;
}

/**
 * `tmpl/block.tmpl` (52 lines), whitespace included so the rendered subtree
 * matches the legacy DOM node-for-node.
 *
 * ★ NOT PORTED — `block.tmpl:42-51`, the "Filter V-Bank" row. It is wrapped in
 * `.hide` (`display:none !important`) and duplicates the Flat Filter row's two
 * values verbatim; discovery §10 lists it as DEAD. No CSS rule in
 * `css/sizer/styles.css` selects `.box-vertical-align` positionally, so dropping
 * it changes nothing visually — and it removes 6 dead nodes from EVERY one of
 * the ~400 live blocks. Same call Builder 1 made for the hidden duplicate half
 * of `settings.tmpl`.
 *
 * The two component rows differ in class ORDER on purpose: the coils render
 * `left {{status}} last-column` (`:17`, `:29`) while the filter renders
 * `left last-column {{status}}` (`:40`) — so an empty `filter` object leaves a
 * trailing space in the attribute. OBSERVED live; reproduced.
 */
function blockTemplate(data: BlockTemplateData): string {
  const cc = data.cooling_coil ?? {};
  const hc = data.heating_coil ?? {};
  const ft = data.filter ?? {};
  return `<div class="body-scroll block" data-page_x="${esc(data.page_x)}" data-page_y="${esc(
    data.page_y
  )}" data-x_on_page="${esc(data.x_on_page)}" data-y_on_page="${esc(
    data.y_on_page
  )}" style="position: absolute;" id="w${esc(data.current_width)}_h${esc(data.current_height)}">
    <div class="td-block">
        <div class="box-vertical-align only-for-selected">
            <div class="left first-column">COMPONENT</div>
            <div class="left second-column">AREA</div>
            <div class="left last-column">VELOCITY</div>
        </div>
        <div class="box-vertical-align">
            <div class="left first-column">
                <div class="only-for-selected">
                    Cooling Coil
                </div>
            </div>

            <div class="left second-column">
                ${esc(cc.face_area)}</div>
            <div class="left ${esc(cc.status)} last-column">
                ${esc(cc.face_velocity)}</div>
        </div>
        <div class="box-vertical-align">
            <div class="left first-column">
               <div class="only-for-selected">
                    Heating Coil
                </div>
            </div>

            <div class="left second-column">
                ${esc(hc.face_area)}</div>
            <div class="left ${esc(hc.status)} last-column">
                ${esc(hc.face_velocity)}</div>
        </div>
        <div class="box-vertical-align">
            <div class="left first-column">
                <div class="only-for-selected">
                    Flat Filter
                </div>
            </div>

            <div class="left second-column">${esc(ft.face_area)}</div>
            <div class="left last-column ${esc(ft.status)}">${esc(ft.face_velocity)}</div>
        </div>
    </div>
</div>`;
}

/** `String.prototype.toDOM` (sizer/mvc/fn/map.js:244-252) — the monkey-patch
 * `render_block` uses at `:481`, as a local helper instead of a global. */
function toDOM(html: string): HTMLElement {
  const host = document.createElement('div');
  host.innerHTML = html;
  return host.firstChild as HTMLElement;
}

// ---------------------------------------------------------------------------

export interface SizerMatrixEngineOptions {
  /** `#main-table-container` — the scroll viewport (matrix.js:234). */
  containerElement: HTMLElement;
  /** `#main-table` — the absolute-positioned block host (matrix.js:238-240). */
  baseElement: HTMLElement;
  /** `#main-table-column-headers` (matrix.js:249-254). */
  columnHeadersElement: HTMLElement;
  /** `#main-table-row-headers` (matrix.js:255-260). */
  rowHeadersElement: HTMLElement;
  /** `#cad_simple_preloader` — z-index 1 while optimals compute (matrix.js:105). */
  preloaderElement: HTMLElement | null;
  cabinetDimensions: ISizerCabinetDimensions;
  /** `models.SizerPagesCount` — `[28, 28]` for cc 1, `[14, 28]` for cc 5. */
  sizerPagesCount: [number, number];
  /** `fn['sizer.pages.size']` — `[10, 10]`. */
  sizerPageSize: [number, number];
  deviation: ISizerDeviation | null;
  optimalWidthAndHeight: ISizerOptimalSize[];
}

export class SizerMatrixEngine {
  // ---- collaborators ------------------------------------------------------
  private readonly containerElement: HTMLElement;
  private readonly baseElement: HTMLElement;
  private readonly columnHeadersElementStyles: CSSStyleDeclaration;
  private readonly rowHeadersElementStyles: CSSStyleDeclaration;
  private readonly preloaderElement: HTMLElement | null;
  private readonly cabinetDimensions: ISizerCabinetDimensions;
  private readonly sizerPagesCount: [number, number];
  private readonly sizerPageSize: [number, number];
  private readonly deviation: ISizerDeviation | null;
  private readonly optimalWidthAndHeight: ISizerOptimalSize[];

  // ---- geometry — matrix.js:54-63 -----------------------------------------
  /** ⚠ `matrix.js:54`: "If you have changed block sizes, then change this too,
   * otherwise pagination won't work." These MUST stay in lockstep with
   * `css/sizer/styles.css` (`.scrollbar-table .block { min-width: 96px }`,
   * `.main-table-row-headers tr { height: 70px }`). */
  readonly blockHeight = 70;
  readonly blockWidth = 96;
  /** overscan rings kept outside the viewport (matrix.js:63). */
  private readonly blocksCountOnSides = 3;
  private readonly containerLeftMargin: number;

  // ---- data model — matrix.js:40-52 ---------------------------------------
  /** `page_y → page_x → rows[10]`. THE cache; wiped by `updateData`. */
  pages: Record<number, Record<number, ISizerMatrixRow[]>> = {};
  /** `"page_x_page_y" → promise`, so two overlapping windows never double-POST. */
  private pendingPages: Record<string, Promise<unknown>> = {};
  /** `page_y → y_on_page → page_x → x_on_page → node`; presence ⇒ rendered. */
  private renderedBlocks: Record<number, Record<number, Record<number, Record<number, HTMLElement>>>> =
    {};
  /** bumped by every `updateData`; `renderBlock` drops older-version work. */
  private currentVersion = 0;

  // ---- live state ---------------------------------------------------------
  /** the POST body, MUTATED IN PLACE by `updateOptimals` (matrix.js:110-111). */
  private settings: ISizerMatrixRequest | null = null;
  /** last page requested — leaks into `updateOptimals`' body (matrix.js:558). */
  private pageCoordinates: [number, number] | undefined;
  private scrolled = false;
  private canCheckScroll = true;
  private pollTimer: number | null = null;
  private destroyed = false;

  /** the optimal OUTSIDE dims returned when `optimal` was sent (viewset:475-487). */
  private width: number | undefined;
  private height: number | undefined;
  private deviationWidthMax: number | undefined;
  private deviationWidthMin: number | undefined;
  private deviationHeightMax: number | undefined;
  private deviationHeightMin: number | undefined;

  constructor(options: SizerMatrixEngineOptions) {
    this.containerElement = options.containerElement;
    this.baseElement = options.baseElement;
    this.preloaderElement = options.preloaderElement;
    this.cabinetDimensions = options.cabinetDimensions;
    this.sizerPagesCount = options.sizerPagesCount;
    this.sizerPageSize = options.sizerPageSize;
    this.deviation = options.deviation;
    this.optimalWidthAndHeight = options.optimalWidthAndHeight;

    // matrix.js:249-263 — the live computed styles legacy keeps a handle on, so
    // a later CSS change is picked up without re-measuring.
    this.columnHeadersElementStyles = window.getComputedStyle(options.columnHeadersElement);
    this.rowHeadersElementStyles = window.getComputedStyle(options.rowHeadersElement);
    this.containerLeftMargin = parseInt(
      window.getComputedStyle(options.containerElement).marginLeft,
      10
    );

    // matrix.js:44-52 — pre-allocate the rendered-block index.
    // ⚠ Verbatim index pairing, quirks included: `page_y` runs to
    // `pagesCount[1]`, `y_on_page` to `pageSize[1]`, `page_x` to
    // `pagesCount[0]` — but the innermost bucket is created for every page_x,
    // NOT for every x_on_page (those keys appear on demand in `renderBlock`).
    for (let pageY = 0; pageY < this.sizerPagesCount[1]; pageY++) {
      this.renderedBlocks[pageY] = {};
      for (let yOnPage = 0; yOnPage < this.sizerPageSize[1]; yOnPage++) {
        this.renderedBlocks[pageY][yOnPage] = {};
        for (let pageX = 0; pageX < this.sizerPagesCount[0]; pageX++) {
          this.renderedBlocks[pageY][yOnPage][pageX] = {};
        }
      }
    }

    // matrix.js:68-72 — the scroll flag the 700 ms poll drains.
    this.containerElement.addEventListener('scroll', this.onScroll);

    // matrix.js:80-88 — the poll. `self.settings` gates it, so nothing happens
    // before the first `updateData`.
    // ★ CLEANED UP in `destroy()` — legacy never clears this interval.
    this.pollTimer = window.setInterval(() => {
      if (!this.settings) return;
      void this.checkScroll();
      // matrix.js:83-86 — the chatbot hand-off: `window.bot_ahu` is consumed
      // (set to null) and auto-sizes the matrix. A LIVE integration.
      const botAhu = (window as unknown as { bot_ahu?: unknown }).bot_ahu;
      if (botAhu) {
        this.updateOptimals(Number(botAhu));
        (window as unknown as { bot_ahu?: unknown }).bot_ahu = null;
      }
    }, 700);
  }

  private onScroll = (): void => {
    this.scrolled = true; // matrix.js:70-72
  };

  /** ★ Not in legacy — see the class docblock. */
  destroy(): void {
    this.destroyed = true;
    if (this.pollTimer != null) window.clearInterval(this.pollTimer);
    this.pollTimer = null;
    this.containerElement.removeEventListener('scroll', this.onScroll);
  }

  // -------------------------------------------------------------------------
  // update_data — matrix.js:92-100
  // -------------------------------------------------------------------------

  /**
   * Every settings/airflow commit lands here. It DROPS EVERY CACHED PAGE and
   * re-POSTs the current viewport — which is why one field change costs 4 POSTs
   * and a Size click costs ~16 (discovery §5 "Performance"). Reproduced: this
   * is the behaviour, not an accident to optimise away.
   *
   * The caller fires this on the `revision` tick of `useSizerSettings`, NOT on a
   * body diff — legacy re-POSTs even when the body is byte-identical.
   */
  async updateData(settings: ISizerMatrixRequest): Promise<void> {
    if (this.destroyed) return;
    this.clearTable([
      [0, 0],
      [-1, -1],
    ]); // :94 — every current_y/x is out of range ⇒ removes all blocks
    this.pendingPages = {}; // :95
    this.pages = {}; // :96
    // A FRESH object: `updateOptimals` mutates `settings` in place, so reusing
    // the caller's would leak `optimal` back into `useSizerSettings`' payload.
    // Legacy gets this for free — `updateMatrixData` builds a new literal every
    // time (main.js:520-562) — and the freshness is load-bearing: it is exactly
    // why `optimal` DISAPPEARS from `load_page` bodies after a settings change
    // and reappears after the next Size (OBSERVED in `p4/network.json`).
    this.settings = { ...settings };
    this.currentVersion += 1; // :98
    await this.loadAndRenderForCurrentCoordinates(); // :99
  }

  /** Whether a first `updateData` has happened (the poll's `self.settings` gate). */
  get hasSettings(): boolean {
    return this.settings != null;
  }

  // -------------------------------------------------------------------------
  // updateOptimals — matrix.js:102-132
  // -------------------------------------------------------------------------

  /**
   * The second call site of `POST /api/v1.0/rpc/sizer_matrix_data/`. Same URL,
   * same body PLUS `optimal: [airflow, optimal_width, optimal_height]`; the
   * response then also carries `width`/`height` (the optimal cabinet OUTSIDE
   * dims), which become the `.activeBorder` deviation window.
   *
   * Quirks reproduced:
   *  - `page_coordinates` is whatever the LAST `load_page` happened to set
   *    (`:111` reading `self.page_coordinates`, written at `:558`) — a leaked
   *    value, not a meaningful request. OBSERVED `[1,1]` on dev3.
   *  - `self.settings` is MUTATED (`:110-111`), so every subsequent `load_page`
   *    body carries `optimal` too until the next `updateData` replaces the
   *    object. OBSERVED: 15 of 39 POSTs carried it after one Size click.
   *  - the preloader's z-index goes to 1 here (`:105`) and is only ever reset by
   *    a SCROLL-driven render reaching its last row (`:443`).
   *  - no error handling: a non-2xx simply skips everything below (`save` has no
   *    `error` callback), leaving the previous deviation window in place.
   *
   * ★ DELIBERATE DEVIATION — the Backbone request-body leak. Legacy calls
   * `blSizerMatrixData.save({...settings})` on a Model that is created ONCE
   * (main.js:94) and never reset, so Backbone's `save` success handler
   * (`model.set(serverAttrs)`) folds the whole RESPONSE — `width`, `height` and
   * the entire `results` array — into the model's attributes, and the NEXT
   * Size click POSTs them straight back. MEASURED on dev3 2026-07-28: the first
   * optimal body is 800 bytes / 32 keys, the second is **52 168 bytes / 35 keys**
   * carrying `width`, `height` and `results`. The backend reads its 27 keys by
   * name (`api/rest_rpc/viewset.py:284-322` is a presence check, then explicit
   * `data['…']` reads) and ignores every extra key, so the leak has ZERO
   * observable effect — it is 51 KB of wasted upload per repeat Size. Echoing
   * server output back into a request is not behaviour worth reproducing; this
   * port always sends the 32 keys. Logged for the tracker as a legacy frontend
   * defect, not a backend ask.
   */
  updateOptimals(airflow: number): void {
    if (this.destroyed || !this.settings) return;
    // :105 — cover the grid while the optimal is computed.
    if (this.preloaderElement) this.preloaderElement.style.zIndex = '1';

    // :106 — `self.deviation.toJSON()[0]`. DEVIATION (defensive, unreachable on
    // any environment with a `CMSDynamicSizerSettings` row — dev3 has one):
    // legacy would throw a TypeError inside the success callback on an empty
    // collection; bailing here leaves the preloader up, which is what legacy's
    // throw would also do.
    const deviation = this.deviation;
    if (!deviation) return;
    const optimals = buildOptimalGraph(this.optimalWidthAndHeight); // :107-108
    const optimal = interpolateExtrapolate(optimals, airflow); // :109

    this.settings.optimal = optimal as [number, number, number]; // :110
    this.settings.page_coordinates = this.pageCoordinates; // :111

    void saveSizerMatrixData({ ...this.settings }).then((incoming) => {
      // Backbone's `success` — skipped entirely on a non-2xx (`null` here).
      if (this.destroyed || !incoming) return;
      this.width = incoming.width; // :114
      this.height = incoming.height; // :115
      const window_ = deviationWindow(this.width as number, this.height as number, deviation);
      this.deviationWidthMax = window_.widthMax; // :117-119
      this.deviationWidthMin = window_.widthMin; // :120-122
      this.deviationHeightMax = window_.heightMax; // :123-125
      this.deviationHeightMin = window_.heightMin; // :126-128
      this.scrollToTheMostOptimal(); // :129
    });
  }

  // -------------------------------------------------------------------------
  // scrollToTheMostOptimal — matrix.js:134-213
  // -------------------------------------------------------------------------

  /**
   * Centre the viewport on the middle of the deviation window.
   *
   * VERIFIED on dev3 2026-07-28 at 1600×950, airflow 20 000
   * (window 83…86 × 91…94, container rect 1508.484 × 731,
   *  row-header width "66.5px", column-header height "50.5px"):
   *   mostOptimalWidth  = (86 + 83) / 2 = 84.5 → X = (84.5 - 12) / 1 = 72.5
   *                     → frac 0.5 ⇒ floor + 0.5 = 72.5
   *   mostOptimalHeight = (94 + 91) / 2 = 92.5 → Y = 80.5 ⇒ 80.5
   *   scrollLeft = (72.5 + 0.5) × 96 - (1508.484 - parseInt("66.5px")) / 2 ≈ 6286.8
   *   scrollTop  = (80.5 + 0.5) × 70 - (731     - parseInt("50.5px")) / 2 ≈ 5329.5
   * OBSERVED `scrollLeft 6287`, `scrollTop 5330`. ✔
   *
   * ⚠ `parseInt` on a computed style TRUNCATES the sub-pixel part ("66.5px" →
   * 66). Kept verbatim — it is sub-pixel, and "fixing" it would move the scroll
   * position by a quarter pixel for no reason.
   */
  scrollToTheMostOptimal(): void {
    const dims = this.cabinetDimensions;
    // `undefined` for every not-yet-computed bound, exactly as legacy reads them
    // off `self` before the first successful `updateOptimals`.
    const wMax = this.deviationWidthMax as number;
    const wMin = this.deviationWidthMin as number;
    const hMax = this.deviationHeightMax as number;
    const hMin = this.deviationHeightMin as number;
    // :137-150 — every guard must hold, else bail with no scroll at all.
    // (Note legacy null-checks only the WIDTH pair; the height pair is covered
    // by `hMax > 0`, which is false for `undefined`. Verbatim.)
    if (
      !(
        this.deviationWidthMax != null &&
        this.deviationWidthMin != null &&
        wMax > 0 &&
        hMax > 0 &&
        wMin < dims.tunnel_cabinet_width_max &&
        hMin < dims.tunnel_cabinet_height_max
      )
    ) {
      return;
    }

    const mostOptimalWidth = (wMax + wMin) / 2; // :152-153
    const mostOptimalHeight = (hMax + hMin) / 2; // :154-155

    const mostOptimalX = snapOptimalIndex(
      optimalAxisIndex(
        mostOptimalWidth,
        dims.tunnel_cabinet_width_min,
        dims.tunnel_cabinet_width_max,
        dims.tunnel_cabinet_width_increment
      )
    ); // :157-177
    const mostOptimalY = snapOptimalIndex(
      optimalAxisIndex(
        mostOptimalHeight,
        dims.tunnel_cabinet_height_min,
        dims.tunnel_cabinet_height_max,
        dims.tunnel_cabinet_height_increment
      )
    ); // :179-199

    const boundingRect = this.containerElement.getBoundingClientRect(); // :201
    this.containerElement.scrollTo(
      (mostOptimalX + 0.5) * this.blockWidth -
        (boundingRect.width - parseInt(this.rowHeadersElementStyles.width, 10)) / 2,
      (mostOptimalY + 0.5) * this.blockHeight -
        (boundingRect.height - parseInt(this.columnHeadersElementStyles.height, 10)) / 2
    ); // :203-212
  }

  // -------------------------------------------------------------------------
  // check_scroll — matrix.js:215-223
  // -------------------------------------------------------------------------

  /** Drained by the 700 ms poll. `canCheckScroll` is a re-entrancy latch: while
   * a windowed render is in flight, further scroll ticks are ignored. */
  private async checkScroll(): Promise<void> {
    if (this.scrolled && this.canCheckScroll) {
      this.canCheckScroll = false;
      this.scrolled = false;
      await this.loadAndRenderForCurrentCoordinates(true);
      this.canCheckScroll = true;
    }
  }

  // -------------------------------------------------------------------------
  // load_and_render_for_current_coordinates — matrix.js:267-306
  // -------------------------------------------------------------------------

  /**
   * THE WINDOW. From the current scroll offset it derives the centre cell, how
   * many cells fit, pads by `blocksCountOnSides` (3) on every side, clamps to
   * the grid, evicts everything more than 25 cells outside, and renders the
   * rest.
   *
   * VERIFIED against dev3 2026-07-28 @1600×950 (rect 1508.484 × 731,
   * `marginLeft: -15px`, column-header height "50.5px"):
   *   containerWidth  = 1508.484 - (-15) = 1523.484
   *   containerHeight = 731 - parseInt("50.5px") = 681
   *   at scroll 0,0 → centre = [round(761.7/96), round(340.5/70)] = [8, 5]
   *   blocksOnPage   = [ceil(1523.484/96), ceil(681/70)] = [16, 10]
   *   area = [[8-8-3, 5-5-3], [8+8+3, 5+5+3]] = [[-3,-3], [19,13]] → clamped
   *          [[0,0],[19,13]] = 20 × 14 = **280 blocks**. OBSERVED: 280. ✔
   *   after the Size scroll the window is unclamped: 23 × 17 = **391**.
   *   OBSERVED: 391. ✔
   */
  private async loadAndRenderForCurrentCoordinates(isScroll?: boolean): Promise<void> {
    if (this.destroyed) return;
    const rect = this.containerElement.getBoundingClientRect(); // :269
    const containerWidth = rect.width - this.containerLeftMargin; // :270-271
    const containerHeight = rect.height - parseInt(this.columnHeadersElementStyles.height, 10); // :272-274
    const scrollTop = this.containerElement.scrollTop; // :275
    const scrollLeft = this.containerElement.scrollLeft; // :276
    const centerCoordinates = [
      Math.round((scrollLeft + containerWidth / 2) / this.blockWidth),
      Math.round((scrollTop + containerHeight / 2) / this.blockHeight),
    ]; // :277-280
    const blocksOnPage = [
      Math.ceil(containerWidth / this.blockWidth),
      Math.ceil(containerHeight / this.blockHeight),
    ]; // :281-284

    const area = shrinkAreaToFitAvailableSpace(
      [
        centerCoordinates[0] - Math.floor(blocksOnPage[0] / 2) - this.blocksCountOnSides,
        centerCoordinates[1] - Math.floor(blocksOnPage[1] / 2) - this.blocksCountOnSides,
      ],
      [
        centerCoordinates[0] + Math.ceil(blocksOnPage[0] / 2) + this.blocksCountOnSides,
        centerCoordinates[1] + Math.ceil(blocksOnPage[1] / 2) + this.blocksCountOnSides,
      ],
      this.cabinetDimensions.width_values.length,
      this.cabinetDimensions.height_values.length
    ); // :286-303

    // :304 — evict everything >25 cells outside the window. The 25-cell skirt is
    // why a scroll gesture ACCUMULATES nodes (280 → 391 → 782 OBSERVED) instead
    // of holding a constant count: blocks are only dropped once they drift well
    // clear, so a short drag re-uses almost everything.
    const keep = enlargeArea(
      area,
      25,
      this.cabinetDimensions.width_values.length,
      this.cabinetDimensions.height_values.length
    );
    if (keep) this.clearTable(keep);

    // :305 — note the missing `await` on the inner render: legacy resolves as
    // soon as the page LOADS are kicked off, not when the DOM is painted, which
    // is what lets the `canCheckScroll` latch reopen early.
    if (area) await this.checkLoadAndRenderArea(area[0], area[1], isScroll);
  }

  // -------------------------------------------------------------------------
  // clear_table — matrix.js:309-341
  // -------------------------------------------------------------------------

  /**
   * Remove every rendered block outside `currentArea` (inclusive bounds).
   * Walks the full `pagesCount × pageSize × pagesCount` index (7 840 buckets for
   * cc 1) on every call — legacy does, and at ~8 k cheap iterations per 700 ms
   * tick it is not the bottleneck.
   *
   * ⚠ Index pairing here is the CORRECT one (`pageSize[1]` for y, `pageSize[0]`
   * for x) — unlike `renderBlock`, which swaps them (see there).
   */
  private clearTable(currentArea: MatrixArea): void {
    for (let pageY = 0; pageY < this.sizerPagesCount[1]; pageY++) {
      for (let yOnPage = 0; yOnPage < this.sizerPageSize[1]; yOnPage++) {
        const currentY = yOnPage + pageY * this.sizerPageSize[1];
        for (let pageX = 0; pageX < this.sizerPagesCount[0]; pageX++) {
          const bucket = this.renderedBlocks[pageY][yOnPage][pageX];
          for (const key of Object.keys(bucket)) {
            const xOnPageRendered = parseInt(key, 10);
            const currentX = xOnPageRendered + pageX * this.sizerPageSize[0];
            if (
              currentY < currentArea[0][1] ||
              currentY > currentArea[1][1] ||
              currentX < currentArea[0][0] ||
              currentX > currentArea[1][0]
            ) {
              const block = bucket[xOnPageRendered];
              delete bucket[xOnPageRendered];
              block.remove();
            }
          }
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // check_load_and_render_area — matrix.js:389-429
  // -------------------------------------------------------------------------

  /**
   * Walk the PAGE rectangle covering the window; kick a `loadPage` for each page
   * not yet cached and render that page's slice as soon as it resolves.
   *
   * Pages render INDEPENDENTLY and out of order — legacy deliberately does not
   * await the loop (`:409` starts a floating `.then`, `:424` never returns it),
   * so the nearest-cached pages paint immediately while the rest stream in. The
   * `version` captured at `:392` is what stops a slow response from painting
   * over a newer `updateData`.
   */
  private async checkLoadAndRenderArea(
    fromCoords: [number, number],
    toCoords: [number, number],
    isScroll?: boolean
  ): Promise<void> {
    const version = this.currentVersion; // :392
    for (
      let pageY = Math.floor(fromCoords[1] / this.sizerPageSize[1]);
      pageY <= Math.floor(toCoords[1] / this.sizerPageSize[1]);
      pageY++
    ) {
      for (
        let pageX = Math.floor(fromCoords[0] / this.sizerPageSize[0]);
        pageX <= Math.floor(toCoords[0] / this.sizerPageSize[0]);
        pageX++
      ) {
        const promisesToWait = [
          !this.pages[pageY] || !this.pages[pageY][pageX] ? this.loadPage(pageX, pageY, true) : null,
        ]; // :404-408
        // Floating on purpose — see the docblock.
        void Promise.all(promisesToWait).then(async () => {
          if (this.destroyed) return;
          const cut = cutAreas(
            [fromCoords, toCoords],
            [
              [pageX * this.sizerPageSize[0], pageY * this.sizerPageSize[1]],
              [
                pageX * this.sizerPageSize[0] + this.sizerPageSize[0] - 1, // sides included
                pageY * this.sizerPageSize[1] + this.sizerPageSize[1] - 1,
              ],
            ]
          ); // :411-420
          if (cut) await this.renderArea(cut[0], cut[1], version, isScroll);
        });
      }
    }
  }

  // -------------------------------------------------------------------------
  // render_area — matrix.js:435-458
  // -------------------------------------------------------------------------

  /** Render `[from…to]` inclusive, one ROW per macrotask so the browser paints
   * between rows (`:456`). Every page in range must already be cached. */
  private async renderArea(
    fromCoords: [number, number],
    toCoords: [number, number],
    version: number,
    isScroll?: boolean
  ): Promise<void> {
    for (let y = fromCoords[1]; y <= toCoords[1]; y++) {
      if (this.destroyed) return;
      const pageY = Math.floor(y / this.sizerPageSize[1]);
      const yOnPage = y % this.sizerPageSize[1];
      for (let x = fromCoords[0]; x <= toCoords[0]; x++) {
        const pageX = Math.floor(x / this.sizerPageSize[0]);
        const xOnPage = x % this.sizerPageSize[0];
        // :443 — the ONLY place the preloader is lowered again, and only on a
        // SCROLL-driven render. After a Size click it stays up until the
        // `scrollToTheMostOptimal` scroll triggers the next windowed render.
        if (isScroll && y == toCoords[1] && this.preloaderElement) {
          this.preloaderElement.style.zIndex = '0';
        }
        if (this.renderedBlocks[pageY][yOnPage][pageX][xOnPage]) continue; // :444-445
        // DEVIATION (defensive, unreachable in the happy path): legacy asserts
        // "ALL POINTS HAS TO BE LOADED!" (`:434`) and dereferences blindly, so a
        // non-2xx page — which `load_page` caches as `undefined` — takes the
        // whole render down with a TypeError. Skipping the row instead leaves
        // the same blank cells without killing the rest of the grid.
        const row = this.pages[pageY]?.[pageX]?.[yOnPage];
        if (!row) continue;
        this.renderBlock(
          pageX,
          pageY,
          xOnPage,
          yOnPage,
          row.current_height,
          row.elements[xOnPage],
          version
        ); // :446-454
      }
      await timeout(1); // :456
    }
  }

  // -------------------------------------------------------------------------
  // render_block — matrix.js:461-511
  // -------------------------------------------------------------------------

  /**
   * One cell. Every line below is deliberate, quirks and all:
   *
   *  1. `:471` the stale guard uses `version &&`, so version 0 never guards —
   *     harmless, `currentVersion` is already 1 by the first render.
   *  2. `:482-485` **THE SWAPPED PAGE-SIZE INDICES**: `top` multiplies by
   *     `pageSize[0]` and `left` by `pageSize[1]`. Both are 10 today so it is
   *     invisible; it would break the moment pages stopped being square.
   *     Reproduced verbatim.
   *  3. `:488` `greyBackground` is added UNCONDITIONALLY, and then the
   *     checkerboard test adds `greyBackground` again or `whiteBackground` —
   *     so HALF THE CELLS CARRY BOTH CLASSES and the checkerboard is decided by
   *     CSS source order (`styles.css:7359` grey, `:7362` white ⇒ white wins),
   *     not by the class logic. OBSERVED live: 195 `.greyBackground.whiteBackground`
   *     vs 196 `.greyBackground` only, out of 391.
   *  4. `:497-502` the deviation test compares the STRING `current_width`
   *     ("84.00") with a number — JS coerces, so it works — and the bounds are
   *     STRICT, so a cell exactly on the boundary is excluded.
   *  5. `:508-510` legacy sets `position:absolute` on `#main-table`, wipes the
   *     whole style attribute, then sets it again — on EVERY block. The net
   *     effect is a constant `#main-table { position: absolute }`, so React sets
   *     it once in the scaffold instead of 400 times a render.
   */
  private renderBlock(
    pageX: number,
    pageY: number,
    xOnPage: number,
    yOnPage: number,
    currentHeight: string,
    data: ISizerCell,
    version: number
  ): void {
    if (version && version < this.currentVersion) return; // :471
    const block = toDOM(
      blockTemplate({
        ...data,
        page_x: pageX,
        page_y: pageY,
        x_on_page: xOnPage,
        y_on_page: yOnPage,
        current_height: currentHeight,
      })
    ); // :472-481

    block.style.top = `${(pageY * this.sizerPageSize[0] + yOnPage) * this.blockHeight}px`; // :482-483
    block.style.left = `${(pageX * this.sizerPageSize[1] + xOnPage) * this.blockWidth}px`; // :484-485
    block.style.height = `${this.blockHeight}px`; // :486
    block.style.width = `${this.blockWidth}px`; // :487
    block.classList.add('greyBackground'); // :488 — unconditional (quirk 3)
    if (
      Number((pageX * this.sizerPageSize[0] + xOnPage) % 2 == 0) ^
      Number((pageY * this.sizerPageSize[1] + yOnPage) % 2 == 0)
    ) {
      block.classList.add('greyBackground'); // :493
    } else {
      block.classList.add('whiteBackground'); // :495
    }
    if (
      (data.current_width as unknown as number) < (this.deviationWidthMax as number) &&
      (data.current_width as unknown as number) > (this.deviationWidthMin as number) &&
      (currentHeight as unknown as number) < (this.deviationHeightMax as number) &&
      (currentHeight as unknown as number) > (this.deviationHeightMin as number)
    ) {
      block.classList.add('activeBorder'); // :497-504
    }
    this.baseElement.append(block); // :505
    this.renderedBlocks[pageY][yOnPage][pageX][xOnPage] = block; // :507
  }

  // -------------------------------------------------------------------------
  // load_page — matrix.js:540-590
  // -------------------------------------------------------------------------

  /**
   * Fetch one 10×10 page. The first half is SYNCHRONOUS on purpose (`:538`): the
   * pending-promise registration happens before any `await`, so two overlapping
   * windows asking for the same page in the same turn produce ONE POST.
   *
   * `:558` sets `self.page_coordinates` on EVERY call — including calls that
   * only join an in-flight load — which is the leak `updateOptimals` then sends.
   */
  private loadPage(pageX: number, pageY: number, safeGet = false): Promise<unknown> {
    // :545-546 — already cached.
    if (this.pages[pageY] && this.pages[pageY][pageX]) {
      return Promise.resolve(this.pages[pageY][pageX]);
    }

    const formattedCoordinates = `${pageX}_${pageY}`; // :548

    // :552-557 — the waiter (`fn.tools.get_waiter`, tools.js:36-44).
    let promise: Promise<unknown> | undefined;
    let finish: ((value: unknown) => void) | undefined;
    if (!this.pendingPages[formattedCoordinates]) {
      promise = new Promise((resolve) => {
        finish = resolve;
      });
      this.pendingPages[formattedCoordinates] = promise;
    }
    this.pageCoordinates = [pageX, pageY]; // :558

    const asyncPart = async (): Promise<unknown> => {
      // :562-567 — another call already owns this page; just wait for it.
      if (!promise) {
        return safeGet ? await this.pendingPages[formattedCoordinates] : null;
      }
      // :570-576 — `fn.tools.data_exchanger`, body = settings + this page.
      const page = await postSizerMatrixData({
        ...(this.settings as ISizerMatrixRequest),
        page_coordinates: [pageX, pageY],
      });
      // :579-580 — cache. (No `response.ok` check in legacy either: an error
      // body simply stores `undefined` and the render skips the row.)
      if (this.pages[pageY] == null) this.pages[pageY] = {};
      this.pages[pageY][pageX] = page.results;
      finish?.(page); // :583
      delete this.pendingPages[formattedCoordinates]; // :584
      return page.results;
    };

    return asyncPart(); // :589
  }
}
